import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from Google OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        return data;
    };

    const signup = async (email, password, name, referral_code) => {
        const { data } = await api.post("/auth/signup", { email, password, name, referral_code });
        setUser(data);
        return data;
    };

    const logout = () => {
        api.post("/auth/logout").catch((err) => {
            console.warn("logout error (continuing):", err?.message || err);
        });
        setUser(null);
        window.location.href = "/";
    };

    const value = useMemo(
        () => ({ user, loading, refresh, login, signup, logout, setUser }),
        [user, loading, refresh]
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

// Format FastAPI 422 validation errors safely
export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
